import React, { useEffect, useRef, useState, useCallback } from "react";
import { MarkdownViewer } from "components/assets/utilities/MarkdownViewer";
import { PieChart, extractPieChartData } from "components/assets/utilities/ChartViews/PieChartView";
import { BarChart, extractBarChartData } from "components/assets/utilities/ChartViews/BarChartView";
import { ScatterPlot, extractScatterPlotData } from "components/assets/utilities/ChartViews/ScatterPlotView";
import { LineGraph, extractLineGraphData } from "components/assets/utilities/ChartViews/LineGraphView";
import { Histogram, extractHistogramData } from "components/assets/utilities/ChartViews/HistogramView";
import { BoxPlot, extractBoxPlotData } from "components/assets/utilities/ChartViews/BoxPlotView";
import { Heatmap, extractHeatmapData } from "components/assets/utilities/ChartViews/HeatmapView";
import { DataTableView } from "components/assets/utilities/ChartViews/DataTableView";
import { escapeCsvValue } from "../../../assets/utilities/PageUtils";
import { useAppSelector } from "redux/hooks";
import { JobViewModel } from "../ViewModels/JobViewModel";

export function ResponseSectionView(props: {
    status: string;
    statusMessage?: string;
    outputState: any;
    loadedOutput: any;
    isOutputLoadFailed: boolean;
    outputLoadError: string | null;
    darkMode: boolean;
    parentJobAlias?: string;
    defaultPageSize: number;
    jobID: string;
    job: JobViewModel;
    responseBoxRef: React.RefObject<HTMLDivElement>;
    tableContainerRef: React.RefObject<HTMLDivElement>;
    ellipsis: string;
    onAnimationStateChange?: (isAnimating: boolean) => void;
    onAddQueryToggle?: (query?: string, queryName?: string) => void;
}) {
    const { status, statusMessage, outputState, loadedOutput, isOutputLoadFailed, outputLoadError, darkMode, parentJobAlias, defaultPageSize, jobID, job, responseBoxRef, tableContainerRef, ellipsis, onAnimationStateChange, onAddQueryToggle } = props;
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [isAnimating, setIsAnimating] = useState(false);
    const animateResponses = useAppSelector(state => state.user.settings?.animate_responses ?? true);

    // Extract the codeblock that appears after "The following SQL query was executed to generate the data:"
    const extractSQLQueryCodeblock = useCallback((markdown: string): string | null => {
        if (!markdown) return null;

        // Search for the text "The following SQL query was executed to generate the data:"
        const searchText = "The following SQL query was executed to generate the data:";
        const searchIndex = markdown.indexOf(searchText);

        if (searchIndex === -1) {
            // If the text is not found, fall back to the last codeblock
            const codeblockRegex = /```[\s\S]*?```/g;
            const matches = markdown.match(codeblockRegex);
            if (!matches || matches.length === 0) {
                return null;
            }
            const lastCodeblock = matches[matches.length - 1];
            const content = lastCodeblock
                .replace(/^```[a-zA-Z]*\n?/, '')
                .replace(/\n?```$/, '')
                .trim();
            return content || null;
        }

        // Find the substring after the search text
        const textAfterSearch = markdown.substring(searchIndex + searchText.length);

        // Find the first codeblock after the search text
        const codeblockRegex = /```[\s\S]*?```/;
        const match = textAfterSearch.match(codeblockRegex);

        if (!match) {
            return null;
        }

        // Remove the ``` markers and optional language identifier
        const content = match[0]
            .replace(/^```[a-zA-Z]*\n?/, '') // Remove opening ``` and optional language
            .replace(/\n?```$/, '') // Remove closing ```
            .trim();

        return content || null;
    }, []);

    // Handler for add query button - for RunNLI, extract codeblock after "The following SQL query was executed to generate the data:"
    const handleAddQuery = useCallback(() => {
        // Open the modal immediately
        if (onAddQueryToggle) {
            onAddQueryToggle(undefined, undefined);
        }

        // Extract query from codeblock after "The following SQL query was executed to generate the data:"
        const responseContent = loadedOutput?.Response?.content || "";
        const query = extractSQLQueryCodeblock(responseContent);

        if (query && onAddQueryToggle) {
            onAddQueryToggle(query);
        }
    }, [loadedOutput, extractSQLQueryCodeblock, onAddQueryToggle]);

    // Track previous response content and job status to detect new responses
    const prevResponseContentRef = useRef<string | undefined>(undefined);
    const prevOutputStatusRef = useRef<string | undefined>(undefined);
    const prevJobStatusRef = useRef<string | undefined>(undefined);
    const prevJobIDRef = useRef<string | undefined>(undefined);
    const hasSeenRunningRef = useRef<boolean>(false);
    const hasAnimatedThisResponseRef = useRef<boolean>(false);
    const [shouldAnimateReveal, setShouldAnimateReveal] = useState(false);

    useEffect(() => {
        if (responseBoxRef.current && status === "RUNNING" && statusMessage) {
            responseBoxRef.current.scrollTo({
                top: responseBoxRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [statusMessage, status, responseBoxRef]);

    // Detect when a new response is generated for the first time ever
    useEffect(() => {
        const currentResponseContent = loadedOutput?.Response?.content;
        const currentOutputStatus = outputState?.status;
        const currentJobStatus = status;
        const currentJobID = jobID;

        // Reset tracking when jobID changes
        if (prevJobIDRef.current !== currentJobID) {
            prevResponseContentRef.current = undefined;
            prevOutputStatusRef.current = undefined;
            prevJobStatusRef.current = currentJobStatus;
            prevJobIDRef.current = currentJobID;
            hasSeenRunningRef.current = currentJobStatus === "RUNNING";
            hasAnimatedThisResponseRef.current = false;
            setShouldAnimateReveal(false);
            return;
        }

        // Track if we've seen the job in RUNNING status
        if (currentJobStatus === "RUNNING") {
            hasSeenRunningRef.current = true;
        }

        // Detect new response: response content appears for the first time
        // AND we've seen the job RUNNING (meaning it was just generated, not loaded from cache)
        const contentAppeared = !prevResponseContentRef.current && currentResponseContent;
        const hasSeenRunning = hasSeenRunningRef.current;
        const isCurrentlyRunning = currentJobStatus === "RUNNING";
        const wasRunning = prevJobStatusRef.current === "RUNNING";
        const isNowCompleted = currentJobStatus !== "RUNNING";
        const contentChanged = prevResponseContentRef.current !== currentResponseContent;

        // This is a new response if:
        // 1. (We've seen the job in RUNNING status OR it's currently RUNNING OR it just transitioned from RUNNING) - meaning it was just generated, not loaded from cache
        // 2. AND response content appeared for the first time (was undefined, now has content)
        // 3. AND we haven't already animated this response
        // 4. AND animate_responses setting is enabled
        const isNewResponse = (hasSeenRunning || isCurrentlyRunning || (wasRunning && isNowCompleted)) && contentAppeared && !hasAnimatedThisResponseRef.current;

        if (isNewResponse && animateResponses) {
            // New response detected - start animation (only if animate_responses is enabled)
            setShouldAnimateReveal(true);
            hasAnimatedThisResponseRef.current = true;
        } else if (!animateResponses) {
            // If animate_responses is disabled, never animate
            setShouldAnimateReveal(false);
        } else if (contentChanged && currentResponseContent) {
            // Content changed to something completely different - reset animation tracking
            hasAnimatedThisResponseRef.current = false;
            setShouldAnimateReveal(false);
        } else if (!currentResponseContent) {
            // Content disappeared - stop animation
            setShouldAnimateReveal(false);
        }
        // Otherwise, if content is the same and we're already animating, keep animating
        // (the MarkdownViewer will handle completing the animation)

        // Update refs
        prevResponseContentRef.current = currentResponseContent;
        prevOutputStatusRef.current = currentOutputStatus;
        prevJobStatusRef.current = currentJobStatus;
        prevJobIDRef.current = currentJobID;
    }, [loadedOutput?.Response?.content, outputState?.status, status, jobID, animateResponses]);

    // Auto-scroll during animation if user is at the bottom
    const prevScrollHeightRef = useRef<number>(0);
    const animationFrameRef = useRef<number | null>(null);
    const isAnimatingRef = useRef<boolean>(false);

    // Helper function to find the actual scrollable parent element
    const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
        if (!element) return null;

        let current: HTMLElement | null = element;
        while (current) {
            const style = window.getComputedStyle(current);
            const overflow = style.overflow;
            const overflowY = style.overflowY;
            const overflowX = style.overflowX;

            // Check if this element is scrollable
            const isScrollable = (overflow === 'auto' || overflow === 'scroll' ||
                                 overflowY === 'auto' || overflowY === 'scroll' ||
                                 overflowX === 'auto' || overflowX === 'scroll') &&
                                current.scrollHeight > current.clientHeight;

            if (isScrollable) {
                return current;
            }

            // Also check window/document scrolling
            if (current === document.body || current === document.documentElement) {
                // Check if window is scrollable
                if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
                    return document.documentElement;
                }
            }

            current = current.parentElement;
        }

        // Fallback: check window
        if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
            return document.documentElement;
        }

        return null;
    };

    // Keep ref in sync with state
    useEffect(() => {
        isAnimatingRef.current = isAnimating;
    }, [isAnimating]);

    useEffect(() => {
        if (!isAnimating || !responseBoxRef.current) {
            prevScrollHeightRef.current = 0;
            // Cancel any ongoing animation frame
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            return;
        }

        const threshold = 150; // pixels - threshold to determine if user is at bottom

        const checkAndScroll = () => {
            // Check current animation state from ref (always up-to-date)
            if (!responseBoxRef.current || !isAnimatingRef.current) {
                animationFrameRef.current = null;
                return;
            }

            // Find the actual scrollable container
            const scrollableContainer = findScrollableParent(responseBoxRef.current);

            if (!scrollableContainer) {
                // Continue checking while animating
                if (isAnimatingRef.current) {
                    animationFrameRef.current = requestAnimationFrame(checkAndScroll);
                } else {
                    animationFrameRef.current = null;
                }
                return;
            }

            const scrollTop = scrollableContainer.scrollTop;
            const clientHeight = scrollableContainer.clientHeight;
            const scrollHeight = scrollableContainer.scrollHeight;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;
            const needsScrolling = Math.abs(scrollTop + clientHeight - scrollHeight) > 5; // 5px tolerance

            // Scroll if user is at bottom (or was at bottom) and needs to be scrolled
            // During animation, use instant scroll for better responsiveness
            if (isAtBottom && needsScrolling) {
                // Use instant scroll during animation for better responsiveness
                scrollableContainer.scrollTop = scrollHeight;
            }

            prevScrollHeightRef.current = scrollHeight;

            // Continue checking while animating
            if (isAnimatingRef.current) {
                animationFrameRef.current = requestAnimationFrame(checkAndScroll);
            } else {
                animationFrameRef.current = null;
            }
        };

        // Start the continuous check
        animationFrameRef.current = requestAnimationFrame(checkAndScroll);

        // Cleanup function
        return () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, [isAnimating, responseBoxRef]);

    return (
        <div ref={responseBoxRef}>
            {status === "RUNNING" && statusMessage
                ? <MarkdownViewer markdown={statusMessage} greyText={true} resetKey={jobID} />
                : status === "RUNNING" && <div style={{opacity: 0.5, color: darkMode ? "#ffffff" : "#000000"}}>{`Processing request${ellipsis}`}</div>
            }
            {outputState.status === "loading" && <div style={{opacity: 0.5, color: darkMode ? "#ffffff" : "#000000"}}>Loading response...</div>}
            {isOutputLoadFailed && <div style={{color: "red"}}>{outputLoadError}</div>}
            {loadedOutput && <>
                {loadedOutput.Error
                    ? <div style={{whiteSpace: "pre-wrap", color: darkMode ? "#ffffff" : "#000000"}}>{loadedOutput.Error.content}</div>
                    : <>
                        {loadedOutput.Data && (() => {
                            const pieChartData = extractPieChartData(
                                loadedOutput.Data.content.columns,
                                loadedOutput.Data.content.dataSource
                            );

                            if (pieChartData) {
                                return (
                                    <div ref={tableContainerRef} style={{marginBottom: "1em"}}>
                                        <PieChart
                                            data={pieChartData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={handleAddQuery}
                                            onDownloadCsv={() => {
                                                if (pieChartData.isDonutChart) {
                                                    // Donut chart CSV
                                                    const csvHeader = "pie_category,pie_value,pie_color,pie_type,pie_title\n";
                                                    const csvRows = pieChartData.slices.map(slice =>
                                                        `${escapeCsvValue(slice.label)},${escapeCsvValue(slice.value)},${escapeCsvValue(slice.color)},donut,${escapeCsvValue(pieChartData.title)}`
                                                    ).join("\n");
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${pieChartData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else {
                                                    // Regular pie chart CSV
                                                    const csvHeader = "pie_category,pie_value,pie_color,pie_title\n";
                                                    const csvRows = pieChartData.slices.map(slice =>
                                                        `${escapeCsvValue(slice.label)},${escapeCsvValue(slice.value)},${escapeCsvValue(slice.color)},${escapeCsvValue(pieChartData.title)}`
                                                    ).join("\n");
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${pieChartData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const barChartData = extractBarChartData(
                                loadedOutput.Data.content.columns,
                                loadedOutput.Data.content.dataSource
                            );

                            if (barChartData) {
                                return (
                                    <div ref={tableContainerRef} style={{marginBottom: "1em"}}>
                                        <BarChart
                                            data={barChartData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={handleAddQuery}
                                            onDownloadCsv={() => {
                                                if (barChartData.isStacked) {
                                                    // Stacked bar chart CSV
                                                    const csvHeader = "bar_category,bar_value,bar_series,bar_color,bar_title,bar_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    barChartData.segments.forEach(segment => {
                                                        const seriesData = barChartData.series.find(s => s.name === segment.series);
                                                        csvRows.push(
                                                            `${escapeCsvValue(segment.category)},${escapeCsvValue(segment.value)},${escapeCsvValue(segment.series)},${escapeCsvValue(seriesData?.color || "")},${escapeCsvValue(barChartData.title)},${escapeCsvValue(barChartData.yAxisLabel || "")}`
                                                        );
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${barChartData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else {
                                                    // Regular bar chart CSV
                                                    const csvHeader = "bar_category,bar_value,bar_color,bar_title,bar_y_axis_label\n";
                                                    const csvRows = barChartData.bars?.map(bar =>
                                                        `${escapeCsvValue(bar.label)},${escapeCsvValue(bar.value)},${escapeCsvValue(bar.color)},${escapeCsvValue(barChartData.title)},${escapeCsvValue(barChartData.yAxisLabel || "")}`
                                                    ).join("\n") || "";
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${barChartData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const scatterPlotData = extractScatterPlotData(
                                loadedOutput.Data.content.columns,
                                loadedOutput.Data.content.dataSource
                            );

                            if (scatterPlotData) {
                                return (
                                    <div ref={tableContainerRef} style={{marginBottom: "1em"}}>
                                        <ScatterPlot
                                            data={scatterPlotData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={handleAddQuery}
                                            onDownloadCsv={() => {
                                                if (scatterPlotData.isBubbleChart) {
                                                    // Bubble chart CSV
                                                    const csvHeader = "scatter_x,scatter_y,scatter_label,scatter_series,scatter_size,scatter_color,scatter_title,scatter_x_axis_label,scatter_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    scatterPlotData.series.forEach(series => {
                                                        series.points.forEach(point => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(point.x)},${escapeCsvValue(point.y)},${escapeCsvValue(point.label)},${escapeCsvValue(series.name)},${escapeCsvValue(point.size !== undefined ? point.size : "")},${escapeCsvValue(series.color)},${escapeCsvValue(scatterPlotData.title)},${escapeCsvValue(scatterPlotData.xAxisLabel || "")},${escapeCsvValue(scatterPlotData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${scatterPlotData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else {
                                                    // Regular scatter plot CSV
                                                    const csvHeader = "scatter_x,scatter_y,scatter_label,scatter_series,scatter_color,scatter_title,scatter_x_axis_label,scatter_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    scatterPlotData.series.forEach(series => {
                                                        series.points.forEach(point => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(point.x)},${escapeCsvValue(point.y)},${escapeCsvValue(point.label)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},${escapeCsvValue(scatterPlotData.title)},${escapeCsvValue(scatterPlotData.xAxisLabel || "")},${escapeCsvValue(scatterPlotData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${scatterPlotData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const lineGraphData = extractLineGraphData(
                                loadedOutput.Data.content.columns,
                                loadedOutput.Data.content.dataSource
                            );

                            if (lineGraphData) {
                                return (
                                    <div ref={tableContainerRef} style={{marginBottom: "1em"}}>
                                        <LineGraph
                                            data={lineGraphData}
                                            darkMode={darkMode}
                                            onAddQuery={handleAddQuery}
                                            onDownloadCsv={() => {
                                                if (lineGraphData.isAreaChart) {
                                                    // Area chart CSV
                                                    const csvHeader = "line_x,line_y,line_series,line_color,line_type,line_title,line_x_axis_label,line_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    lineGraphData.series.forEach(series => {
                                                        series.points.forEach(point => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(point.x)},${escapeCsvValue(point.y)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},area,${escapeCsvValue(lineGraphData.title)},${escapeCsvValue(lineGraphData.xAxisLabel || "")},${escapeCsvValue(lineGraphData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${lineGraphData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else {
                                                    // Regular line graph CSV
                                                    const csvHeader = "line_x,line_y,line_series,line_color,line_title,line_x_axis_label,line_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    lineGraphData.series.forEach(series => {
                                                        series.points.forEach(point => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(point.x)},${escapeCsvValue(point.y)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},${escapeCsvValue(lineGraphData.title)},${escapeCsvValue(lineGraphData.xAxisLabel || "")},${escapeCsvValue(lineGraphData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${lineGraphData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const histogramData = extractHistogramData(
                                loadedOutput.Data.content.columns,
                                loadedOutput.Data.content.dataSource
                            );

                            if (histogramData) {
                                return (
                                    <div ref={tableContainerRef} style={{marginBottom: "1em"}}>
                                        <Histogram
                                            data={histogramData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={handleAddQuery}
                                            onDownloadCsv={() => {
                                                if (histogramData.isGrouped && histogramData.series) {
                                                    // Grouped histogram CSV
                                                    const csvHeader = "hist_bin,hist_count,hist_frequency,hist_y_type,hist_series,hist_color,hist_title,hist_x_axis_label,hist_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    histogramData.series.forEach(series => {
                                                        series.bins.forEach(bin => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(bin.bin)},${escapeCsvValue(bin.count)},${escapeCsvValue(bin.frequency)},${escapeCsvValue(histogramData.yType)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},${escapeCsvValue(histogramData.title)},${escapeCsvValue(histogramData.xAxisLabel || "")},${escapeCsvValue(histogramData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${histogramData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else if (histogramData.bins) {
                                                    // Regular histogram CSV
                                                    const csvHeader = "hist_bin,hist_count,hist_frequency,hist_y_type,hist_color,hist_title,hist_x_axis_label,hist_y_axis_label\n";
                                                    const csvRows = histogramData.bins.map(bin =>
                                                        `${escapeCsvValue(bin.bin)},${escapeCsvValue(bin.count)},${escapeCsvValue(bin.frequency)},${escapeCsvValue(histogramData.yType)},${escapeCsvValue(bin.color)},${escapeCsvValue(histogramData.title)},${escapeCsvValue(histogramData.xAxisLabel || "")},${escapeCsvValue(histogramData.yAxisLabel || "")}`
                                                    ).join("\n");
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${histogramData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const boxPlotData = extractBoxPlotData(
                                loadedOutput.Data.content.columns,
                                loadedOutput.Data.content.dataSource
                            );

                            if (boxPlotData) {
                                return (
                                    <div ref={tableContainerRef} style={{marginBottom: "1em"}}>
                                        <BoxPlot
                                            data={boxPlotData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={handleAddQuery}
                                            onDownloadCsv={() => {
                                                if (boxPlotData.isGrouped && boxPlotData.series) {
                                                    // Grouped box plot CSV
                                                    const csvHeader = "box_category,box_value,box_series,box_color,box_title,box_x_axis_label,box_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    boxPlotData.series.forEach(series => {
                                                        series.boxes.forEach(box => {
                                                            box.values.forEach(value => {
                                                                csvRows.push(
                                                                    `${escapeCsvValue(box.category)},${escapeCsvValue(value)},${escapeCsvValue(series.name)},${escapeCsvValue(series.color)},${escapeCsvValue(boxPlotData.title)},${escapeCsvValue(boxPlotData.xAxisLabel || "")},${escapeCsvValue(boxPlotData.yAxisLabel || "")}`
                                                                );
                                                            });
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${boxPlotData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                } else if (boxPlotData.boxes) {
                                                    // Regular box plot CSV
                                                    const csvHeader = "box_category,box_value,box_color,box_title,box_x_axis_label,box_y_axis_label\n";
                                                    const csvRows: string[] = [];
                                                    boxPlotData.boxes.forEach(box => {
                                                        box.values.forEach(value => {
                                                            csvRows.push(
                                                                `${escapeCsvValue(box.category)},${escapeCsvValue(value)},${escapeCsvValue(box.color)},${escapeCsvValue(boxPlotData.title)},${escapeCsvValue(boxPlotData.xAxisLabel || "")},${escapeCsvValue(boxPlotData.yAxisLabel || "")}`
                                                            );
                                                        });
                                                    });
                                                    const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows.join("\n"))}`;
                                                    const link = document.createElement('a');
                                                    link.setAttribute('href', csvContent);
                                                    const filename = `${boxPlotData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                    link.setAttribute('download', filename);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            }

                            const heatmapData = extractHeatmapData(
                                loadedOutput.Data.content.columns,
                                loadedOutput.Data.content.dataSource
                            );

                            if (heatmapData) {
                                return (
                                    <div ref={tableContainerRef} style={{marginBottom: "1em"}}>
                                        <Heatmap
                                            data={heatmapData}
                                            darkMode={darkMode}
                                            jobAlias={parentJobAlias}
                                            onAddQuery={handleAddQuery}
                                            onDownloadCsv={() => {
                                                // Heatmap CSV
                                                const csvHeader = "heatmap_x,heatmap_y,heatmap_value,heatmap_title,heatmap_x_axis_label,heatmap_y_axis_label\n";
                                                const csvRows = heatmapData.cells.map(cell =>
                                                    `${escapeCsvValue(cell.x)},${escapeCsvValue(cell.y)},${escapeCsvValue(cell.value)},${escapeCsvValue(heatmapData.title)},${escapeCsvValue(heatmapData.xAxisLabel || "")},${escapeCsvValue(heatmapData.yAxisLabel || "")}`
                                                ).join("\n");
                                                const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(csvHeader + csvRows)}`;
                                                const link = document.createElement('a');
                                                link.setAttribute('href', csvContent);
                                                const filename = `${heatmapData.title.replace(/[<>:"/\\|?*]/g, '')}.csv`;
                                                link.setAttribute('download', filename);
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            }}
                                        />
                                    </div>
                                );
                            }

                            return (
                                <DataTableView
                                    columns={loadedOutput.Data.content.columns}
                                    dataSource={loadedOutput.Data.content.dataSource}
                                    darkMode={darkMode}
                                    parentJobAlias={parentJobAlias}
                                    defaultPageSize={defaultPageSize}
                                    tableContainerRef={tableContainerRef}
                                    onAddQuery={handleAddQuery}
                                />
                            );
                        })()}
                        {loadedOutput.Response ? (
                            <MarkdownViewer
                                markdown={loadedOutput.Response.content}
                                resetKey={jobID}
                                animateReveal={shouldAnimateReveal}
                                onAnimationStateChange={(animating) => {
                                    setIsAnimating(animating);
                                    onAnimationStateChange?.(animating);
                                }}
                            />
                        ) : !loadedOutput.Data ? (
                            <div style={{color: "red"}}>The job completed but no response was generated.</div>
                        ) : null}
                    </>
                }
            </>}
        </div>
    );
};

